import { User } from 'firebase/auth';

/**
 * Check if a user is an admin by calling backend API
 * This uses firebase-admin on the server side for better security
 */
export async function isAdmin(user: User | null): Promise<boolean> {
  if (!user) return false;

  try {
    // Get the user's ID token
    const idToken = await user.getIdToken();

    // Call backend API to check admin status
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    const response = await fetch(`${apiBaseUrl}/api/admin/check`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ idToken }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to check admin status:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
      });
      return false;
    }

    const data = await response.json();
    return data.isAdmin === true;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}

/**
 * Get admin users list (admin only)
 * Note: This still uses client-side Firestore access
 * For production, consider creating an API route for this too
 */
export async function getAdminUsers(): Promise<string[]> {
  try {
    // For now, we'll keep this client-side since it's only used in debug page
    // In production, you might want to create an API route for this too
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('./firebase');
    
    const adminDocRef = doc(db, 'settings', 'admin');
    const adminDoc = await getDoc(adminDocRef);

    if (!adminDoc.exists()) {
      return [];
    }

    return adminDoc.data()?.users || [];
  } catch (error) {
    console.error('Error fetching admin users:', error);
    return [];
  }
}

